import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, RefreshCw, Tag } from 'lucide-react'
import {
  jimiService, type JimiTagHistoryRow, type JimiTagRow,
} from '../services/jimiService'

// =============================================================================
// AssetTagsWindow — paired BLE peripherals (driver tags, cargo / temperature
// tags, …) for a JIMI device attached to an asset.
// =============================================================================
//
// Per the P8 plan: list paired tags with last reading; expanding a row
// shows that tag's history (paginated). The tag list is shared by the
// device-centric console; here we just pre-scope to one IMEI.
// =============================================================================

export interface AssetTagsWindowProps {
  assetId: string
  imei: string
  deviceName?: string | null
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

const AssetTagsWindow: React.FC<AssetTagsWindowProps> = ({
  assetId, imei, deviceName, showMessage,
}) => {
  const { t } = useTranslation()
  const [tags, setTags] = useState<JimiTagRow[]>([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [historyByTag, setHistoryByTag] = useState<Record<string, JimiTagHistoryRow[]>>({})
  const [historyLoading, setHistoryLoading] = useState<Record<string, boolean>>({})

  const notify = useCallback((msg: string, type: 'success' | 'error' | 'warning' | 'info') => {
    if (showMessage) showMessage(msg, type)
    else if (type === 'error') console.warn(msg)
  }, [showMessage])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const r = await jimiService.getTags(imei)
      const list = (r as any)?.data?.records || r?.records || []
      setTags(list)
    } catch (err: any) {
      notify(t('asset_tags_load_error', 'Failed to load tags: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setLoading(false) }
  }, [imei, notify, t])

  const loadHistory = useCallback(async (tagId: string) => {
    setHistoryLoading((m) => ({ ...m, [tagId]: true }))
    try {
      const r = await jimiService.getTagHistory(imei, tagId, { size: 50 })
      const list = (r as any)?.data?.records || r?.records || []
      setHistoryByTag((m) => ({ ...m, [tagId]: list }))
    } catch (err: any) {
      notify(t('asset_tags_history_error', 'Failed to load tag history: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally {
      setHistoryLoading((m) => ({ ...m, [tagId]: false }))
    }
  }, [imei, notify, t])

  useEffect(() => { reload() }, [reload])

  const toggle = useCallback((tagId: string) => {
    setExpanded((m) => {
      const next = { ...m, [tagId]: !m[tagId] }
      if (next[tagId] && !historyByTag[tagId]) loadHistory(tagId)
      return next
    })
  }, [historyByTag, loadHistory])

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-300 flex items-center justify-center">
            <Tag className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-sm">{deviceName || t('asset_tags_default_name', 'Paired Tags')}</div>
            <div className="text-xs text-gray-500 font-mono">IMEI {imei} · ASSET {assetId}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={reload}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5"
          title={t('asset_tags_refresh', 'Refresh') as string}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
        {!loading && tags.length === 0 && (
          <div className="text-sm text-gray-500 italic">{t('asset_tags_empty', 'No paired tags reported by this device.')}</div>
        )}
        {!loading && tags.length > 0 && (
          <div className="space-y-2">
            {tags.map((tag) => {
              const open = !!expanded[tag.tagId]
              const rows = historyByTag[tag.tagId] || []
              return (
                <div key={tag.tagId} className="rounded-lg border border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => toggle(tag.tagId)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <div className="flex items-center gap-2">
                      {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      <div className="text-left">
                        <div className="text-sm font-medium">{tag.name || tag.tagId}</div>
                        <div className="text-xs text-gray-500 font-mono">
                          {tag.type ? `${tag.type} · ` : ''}{tag.tagId}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {tag.value == null ? '—' : `${tag.value}${tag.unit ? ' ' + tag.unit : ''}`}
                        </span>
                      </div>
                      <div>{tag.lastSeen ? new Date(tag.lastSeen).toLocaleString() : '—'}</div>
                    </div>
                  </button>
                  {open && (
                    <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-800">
                      {historyLoading[tag.tagId] && <div className="text-xs text-gray-500 mt-2">{t('loading', 'Loading…')}</div>}
                      {!historyLoading[tag.tagId] && rows.length === 0 && (
                        <div className="text-xs text-gray-500 italic mt-2">{t('asset_tags_history_empty', 'No history rows.')}</div>
                      )}
                      {!historyLoading[tag.tagId] && rows.length > 0 && (
                        <table className="min-w-full text-xs mt-2">
                          <thead className="text-gray-500">
                            <tr>
                              <th className="text-left py-1">{t('asset_tags_col_time', 'Time')}</th>
                              <th className="text-left py-1">{t('asset_tags_col_value', 'Value')}</th>
                              <th className="text-left py-1">{t('asset_tags_col_rssi', 'RSSI')}</th>
                              <th className="text-left py-1">{t('asset_tags_col_battery', 'Battery')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, idx) => (
                              <tr key={idx} className="border-t border-gray-100 dark:border-gray-800">
                                <td className="py-1 text-gray-500">{r.ts ? new Date(r.ts).toLocaleString() : '—'}</td>
                                <td className="py-1">{r.value == null ? '—' : String(r.value)}{tag.unit ? ` ${tag.unit}` : ''}</td>
                                <td className="py-1">{r.rssi ?? '—'}</td>
                                <td className="py-1">{r.battery ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetTagsWindow
